import React from 'react'
import { Row } from 'reactstrap';
import { Select, DatePicker } from 'antd';
import { auth } from '../../Api';
import { CirclesWithBar } from 'react-loader-spinner'
import "antd/dist/antd.css";
import moment from "moment";
import {
    EMPTY_MESSAGE, PERIODS, DEFAULT_PERIOD, periodRange, tiles, heatGrid, heatColour, heatTextDark, HEAT_TEXT_DARK, HEAT_TEXT_LIGHT, heatTooltip, hourLabel,
    guardLists, isWarning, formatPct, weekLabel, weekTooltip, lineChart, hasWeeks, optionListed
} from '../../utils/alertStats';
import './dashboard-tab-graphics.scss';

const { RangePicker } = DatePicker;
const PERIOD_KEY = 'graphicsPeriod';

// The heat map of missed alerts: a row per day of the week, a column per hour that had duties (in each location's
// own time zone); hovering or tapping a square shows its day, hour and count
class HeatMap extends React.Component {
    state = { active: null };

    render() {
        const { heatmap } = this.props;
        const grid = heatGrid(heatmap);
        if (!grid.columns.length) {
            return <p className="graphics-empty">{EMPTY_MESSAGE}</p>;
        }
        const { active } = this.state;
        return (
            <div>
                {grid.max === 0 && <p className="graphics-note">No missed alerts in this period.</p>}
                <div className="heatmap-scroll">
                    <table className="heatmap" onMouseLeave={() => this.setState({ active: null })}>
                        <thead>
                            <tr>
                                <th scope="col"><span className="sr-only">Day</span></th>
                                {grid.columns.map((hour) => <th key={hour} scope="col" className="heatmap-hour">{hour < 10 ? '0' + hour : hour}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {grid.rows.map((row) => (
                                <tr key={row.day}>
                                    <th scope="row" className="heatmap-day">{row.short}</th>
                                    {row.cells.map((cell) => {
                                        const key = row.day + ':' + cell.hour;
                                        const text = heatTooltip(row.day, cell.hour, cell.missed);
                                        return (
                                            <td key={cell.hour} className="heatmap-cell" tabIndex={0} aria-label={text}
                                                onMouseEnter={() => this.setState({ active: key })}
                                                onFocus={() => this.setState({ active: key })}
                                                onClick={() => this.setState({ active: active === key ? null : key })}>
                                                <span className="heatmap-square" style={{ background: heatColour(cell.missed, grid.max), color: heatTextDark(cell.missed, grid.max) ? HEAT_TEXT_DARK : HEAT_TEXT_LIGHT }}>
                                                    {cell.missed || ''}
                                                </span>
                                                {active === key && <span className={'heatmap-tooltip' + (row.day >= 5 ? ' heatmap-tooltip-up' : '')} role="tooltip">{text}</span>}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="heatmap-legend" aria-hidden="true">
                    <span>Fewer</span>
                    {[0, 0.25, 0.5, 0.75, 1].map((share) => <span key={share} className="heatmap-legend-box" style={{ background: heatColour(share * Math.max(grid.max, 1), Math.max(grid.max, 1)) }} />)}
                    <span>More missed alerts</span>
                </div>
                <p className="graphics-note">Hours are in each duty location's own time zone. Only hours that had duties are shown.</p>
            </div>
        );
    }
}

function GuardBars({ title, guards }) {
    return (
        <div className="guard-bars">
            {title && <h5 className="graphics-subtitle">{title}</h5>}
            <ol className="guard-bars-list">
                {guards.map((guard) => (
                    <li key={guard.id} className="guard-bar">
                        <span className="guard-bar-name" title={guard.name}>{guard.name}</span>
                        <span className="guard-bar-track">
                            <span className={'guard-bar-fill' + (isWarning(guard.answered_pct) ? ' guard-bar-warning' : '')}
                                style={{ width: Math.max(2, Number(guard.answered_pct)) + '%' }} />
                            <span className="guard-bar-value">{formatPct(guard.answered_pct)}</span>
                        </span>
                        <span className="sr-only">{guard.answered} of {guard.total} alerts answered</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function BestAndWorst({ guards, total, minAlerts }) {
    if (!total) {
        return <p className="graphics-empty">{EMPTY_MESSAGE}</p>;
    }
    const lists = guardLists(guards);
    const note = <p className="graphics-note">Only guards with at least {minAlerts} alerts in the period are ranked. Guards under 90% are shown in orange.</p>;
    if (lists.single !== null && lists.single.length === 0) {
        return <p className="graphics-empty">No guard had {minAlerts} alerts or more in this period.</p>;
    }
    if (lists.single !== null) {
        return <div><GuardBars title="Guards ranked by alerts answered" guards={lists.single} />{note}</div>;
    }
    return (
        <div>
            <div className="guard-columns">
                <GuardBars title="Best guards" guards={lists.best} />
                <GuardBars title="Worst guards" guards={lists.worst} />
            </div>
            {note}
        </div>
    );
}

// Alerts answered week by week (the whole selection), with the dashed target line; hovering or tapping a point shows it
class WeekChart extends React.Component {
    state = { active: null };

    render() {
        const { weeks, target } = this.props;
        if (!hasWeeks(weeks)) {
            return <p className="graphics-empty">{EMPTY_MESSAGE}</p>;
        }
        // A phone: a narrower drawing, so its text is not shrunk
        const narrow = typeof window !== 'undefined' && window.innerWidth < 576;
        const width = narrow ? 340 : 640;
        const height = narrow ? 220 : 240;
        const pad = { top: 16, right: 16, bottom: 34, left: 44 };
        const chart = lineChart(weeks, target, width, height, pad);
        const { active } = this.state;
        const activePoint = chart.points.find((p) => p.index === active);
        const labelEvery = Math.ceil(weeks.length / (narrow ? 3 : 6));
        return (
            <div className="week-chart">
                <svg viewBox={'0 0 ' + width + ' ' + height} role="img" aria-label="Alerts answered, week by week" onMouseLeave={() => this.setState({ active: null })}>
                    {chart.ticks.map((tick) => (
                        <g key={tick.value}>
                            <line x1={pad.left} x2={width - pad.right} y1={tick.y} y2={tick.y} className="week-chart-grid" />
                            <text x={pad.left - 6} y={tick.y + 4} textAnchor="end" className="week-chart-axis">{tick.value}%</text>
                        </g>
                    ))}
                    <line x1={pad.left} x2={width - pad.right} y1={chart.targetY} y2={chart.targetY} className="week-chart-target" />
                    {weeks.map((week, i) => (i % labelEvery === (weeks.length - 1) % labelEvery
                        ? <text key={week.from} x={chart.x(i)} y={height - 10} textAnchor="middle" className="week-chart-axis">{weekLabel(week).split(' – ')[0]}</text>
                        : null))}
                    <path d={chart.path} className="week-chart-line" />
                    {chart.points.map((point) => (
                        <circle key={point.index} cx={point.x} cy={point.y} r={active === point.index ? 7 : 5} className="week-chart-point" tabIndex={0}
                            onMouseEnter={() => this.setState({ active: point.index })}
                            onFocus={() => this.setState({ active: point.index })}
                            onClick={() => this.setState({ active: active === point.index ? null : point.index })}>
                            <title>{weekTooltip(point.week)}</title>
                        </circle>
                    ))}
                </svg>
                <p className="week-chart-tooltip" aria-live="polite">
                    {activePoint ? weekTooltip(activePoint.week) : 'Hover over or tap a point to see its week.'}
                </p>
                <div className="week-chart-legend">
                    <span><span className="week-chart-key week-chart-key-line" />Alerts answered</span>
                    <span><span className="week-chart-key week-chart-key-target" />Target {target}%</span>
                </div>
            </div>
        );
    }
}

class DashboardTabGraphics extends React.Component {
    constructor(props) {
        super(props);

        var selectType = localStorage.getItem('selectType')
        var whichCompany = localStorage.getItem('whichCompany')
        let companyId = localStorage.getItem('companyId')

        if (whichCompany != null && companyId != null) {
            if (whichCompany != companyId) {
                localStorage.setItem('whichCompany', companyId)
                selectType = 'company:company:' + companyId
            } else {
                if (selectType == null) {
                    selectType = 'company:company:' + companyId
                }
            }
        }
        if (selectType == null) {
            selectType = 'company:company:' + companyId
        }
        let period = DEFAULT_PERIOD
        try {
            const saved = localStorage.getItem(PERIOD_KEY)
            if (PERIODS.some((p) => p.value === saved)) {
                period = saved
            }
        } catch (e) {
            // a remembered period is only a convenience
        }

        this.state = {
            selectType: selectType,
            period: period,
            custom: null,
            stats: null,
            loadingData: false,
            error: null,
        }
    }

    componentDidMount() {
        this.load()
    }

    componentWillUnmount() {
        this.unmounted = true
    }

    load(changes = {}) {
        const state = { ...this.state, ...changes }
        const range = periodRange(state.period, Date.now(), state.custom)
        const [type, , id] = String(state.selectType).split(':')
        const asked = (this.asked || 0) + 1
        this.asked = asked
        this.setState({ ...changes, loadingData: true, error: null })
        auth.getAlertStats({ company_id: localStorage.getItem('companyId'), type, id, from: range.from, to: range.to })
            .then((res) => {
                if (this.unmounted || asked !== this.asked) {
                    return
                }
                if (res.data && res.data.code == 200 && res.data.stats) {
                    this.setState({ stats: res.data.stats, loadingData: false })
                } else {
                    const error = res.data && res.data.message && res.data.message.error
                    this.setState({ stats: null, loadingData: false, error: typeof error === 'string' ? error : 'The graphics could not be loaded.' })
                }
            })
            .catch(() => {
                if (!this.unmounted && asked === this.asked) {
                    this.setState({ stats: null, loadingData: false, error: 'The graphics could not be loaded. Please try again.' })
                }
            })
    }

    onSelectChange = (value) => {
        localStorage.setItem('selectType', value);
        this.load({ selectType: value })
    };

    onPeriodChange = (value) => {
        try {
            localStorage.setItem(PERIOD_KEY, value)
        } catch (e) {
            // a remembered period is only a convenience
        }
        this.load({ period: value, custom: null })
    };

    onRangeChange = (dates) => {
        if (!dates || !dates[0] || !dates[1]) {
            this.onPeriodChange(DEFAULT_PERIOD)
            return
        }
        this.load({ period: 'custom', custom: { from: dates[0].clone().startOf('day').valueOf(), to: dates[1].clone().endOf('day').valueOf() + 1 } })
    };

    render() {
        const { selectType, period, custom, stats, loadingData, error } = this.state
        // The dashboard's selector (loaded after this tab opens)
        const selectOptions = this.props.selectOptions
        const periodOptions = PERIODS.map((p) => ({ value: p.value, label: p.label }))
        if (period === 'custom') {
            periodOptions.push({ value: 'custom', label: 'Chosen dates' })
        }

        return <div className="dashboard-graphics">
            <Row className='toolbar graphics-filters pt-3 pb-3'>
                <Select className="graphics-filter" value={period} onChange={this.onPeriodChange} options={periodOptions} aria-label="Period" />
                <RangePicker
                    className="graphics-filter"
                    popupClassName="range-picker-mobiliis"
                    value={custom ? [moment(custom.from), moment(custom.to - 1)] : null}
                    disabledDate={(day) => day && day.isAfter(moment().endOf('day'))}
                    onChange={this.onRangeChange}
                />
                {/* The option's own name ("All my guards", a client's name) once the dashboard's list has loaded */}
                <Select className="graphics-filter" value={optionListed(selectOptions, selectType) ? selectType : selectType.split(":")[1]}
                    onChange={this.onSelectChange} options={selectOptions} aria-label="Show" />
                {loadingData && <span className="table-title center-center graphics-loading">&nbsp;Loading
                    <CirclesWithBar height="25" width="25" radius="3" color="green" ariaLabel="loading" wrapperStyle wrapperClass /></span>}
            </Row>
            {error && <p className="graphics-error" role="alert">{error}</p>}
            {stats && <div>
                <div className="graphics-tiles">
                    {tiles(stats, period).map((tile) => (
                        <div key={tile.key} className="graphics-tile">
                            <div className="graphics-tile-label">{tile.label}</div>
                            <div className="graphics-tile-value">{tile.value}</div>
                            <div className="graphics-tile-sub">{tile.sub}</div>
                            {tile.change && <div className="graphics-tile-change">{tile.change}</div>}
                        </div>
                    ))}
                </div>
                <section className="graphics-card">
                    <h4 className="graphics-title">When are alerts missed?</h4>
                    {stats.tiles.total ? <HeatMap heatmap={stats.heatmap} /> : <p className="graphics-empty">{EMPTY_MESSAGE}</p>}
                </section>
                <section className="graphics-card">
                    <h4 className="graphics-title">Alerts answered by guard</h4>
                    <BestAndWorst guards={stats.guards} total={stats.tiles.total} minAlerts={stats.min_guard_alerts} />
                </section>
                <section className="graphics-card">
                    <h4 className="graphics-title">Alerts answered, week by week</h4>
                    <WeekChart weeks={stats.weeks} target={stats.target_pct} />
                </section>
            </div>}
        </div>
    }
}

import { connect } from "react-redux";
import { setFilterType } from "../../redux/dashboard/action";

const mapStateToProps = (state, ownProps) => ({
    filterType: state.dashboard.filterType,
    selectOptions: ownProps.selectOptions
});

const mapDispatchToProps = dispatch => ({
    setFilterType: (data) => dispatch(setFilterType(data)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DashboardTabGraphics);
